<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <!-- Header -->
      <div class="d-flex align-center mb-6">
        <h2 class="font-weight-bolder">{{ bank.title || $t('finance.banks.bankDetails') }}</h2>
        <v-spacer></v-spacer>
        <v-btn color="primary" class="mr-4" @click="edit">
          {{ $t('finance.banks.edit') }}
        </v-btn>
        <v-btn color="primary" @click="back">
          {{ $t('finance.banks.back') }}
        </v-btn>
      </div>

      <!-- Top Card: Bank Info -->
      <v-card elevation="0" class="mb-6 pa-6">
        <v-row>
          <v-col cols="12">
            <label class="font-weight-bold mb-2 d-block">{{ $t('finance.banks.bank') }}</label>
            <div class="d-flex align-center">
              <img
                :src="bank.logo || 'https://ui-avatars.com/api/?name=Trustbank+Amanah&background=random'"
                style="width: 40px; height: 40px; border-radius: 8px; object-fit: contain;"
                :alt="$t('finance.banks.bankLogo')"
                class="mr-3"
              >
              <span class="text-body-1 font-weight-medium">{{ bank.name }}</span>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Contact Details -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.contactDetails') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class="mr-1">{{ $t('finance.banks.firstName') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.firstNameInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="text-body-1 font-weight-bold">{{ bank.contactFirstName }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.lastName') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.lastNameInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="text-body-1 font-weight-bold">{{ bank.contactLastName }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.email') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.emailInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="text-body-1 font-weight-bold">{{ bank.contactEmail || $t('finance.banks.notAvailable') }}</div>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.phone') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.phoneInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="text-body-1 font-weight-bold">{{ bank.contactPhoneNo }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- API/Integration Info -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.apiIntegrationInfo') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.apiBaseUrl') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.apiBaseUrlInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.apiBaseUrl"
              readonly
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.clientId') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.clientIdInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.apiClientId"
              readonly
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.clientSecret') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.clientSecretInfo') }}</span>
              </v-tooltip>
            </div>
            <div class="d-flex align-center">
              <v-text-field
                v-model="bank.apiClientSecret"
                :type="showClientSecret ? 'text' : 'password'"
                readonly
                outlined
                dense
                hide-details="auto"
                background-color="grey lighten-4"
                class="mr-2"
              >
                <template v-slot:append>
                  <v-icon @click="showClientSecret = !showClientSecret">
                    {{ showClientSecret ? 'mdi-eye' : 'mdi-eye-off' }}
                  </v-icon>
                </template>
              </v-text-field>
              <v-btn small outlined color="primary" class="mr-2" @click="copyToClipboard(bank.apiClientSecret)">
                <v-icon left small>mdi-content-copy</v-icon>
                {{ $t('finance.banks.copy') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Account/Admin Credentials -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.accountAdminCredentials') }}</h3>
        <v-row>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.adminUsername') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.adminUsernameInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.adminUsername"
              readonly
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <div class="d-flex align-center mb-2">
              <label class=" mr-1">{{ $t('finance.banks.password') }}</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="primary" v-bind="attrs" v-on="on">mdi-information</v-icon>
                </template>
                <span>{{ $t('finance.banks.passwordInfo') }}</span>
              </v-tooltip>
            </div>
            <v-text-field
              v-model="bank.adminPasswd"
              :type="showPassword ? 'text' : 'password'"
              readonly
              outlined
              dense
              hide-details="auto"
              background-color="grey lighten-4"
            >
              <template v-slot:append>
                <v-icon @click="showPassword = !showPassword">
                  {{ showPassword ? 'mdi-eye' : 'mdi-eye-off' }}
                </v-icon>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <v-divider class="my-6"></v-divider>

        <!-- Require 2FA -->
        <h3 class="font-weight-bold mb-4">{{ $t('finance.banks.require2FA') }}</h3>
        <div class="d-flex align-center">
          <span class="mr-4">{{ $t('finance.banks.enable') }}</span>
          <v-switch
            v-model="bank.require2fa"
            color="primary"
            inset
            hide-details
            class="mt-0 pt-0"
            readonly
          ></v-switch>
        </div>

      </v-card>
    </v-container>
  </div>
</template>

<script>
import FinanceService from "@/_services/FinanceService";
import LoadingMixin from "@/mixins/LoadingMixin";
export default {
  created() {
    this.fetchBankDetails();
  },
  data() {
    return {
      bankId: this.$route.params.id,
      breadcrumbs: [
        {
          text: this.$t("finance.banks.title"),
          disabled: false,
          href: "/finance-management/banks",
        },
        {
          text: this.$t("finance.banks.bankDetails"),
          disabled: true,
        },
      ],
      bank: {
        name: '',
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhoneNo: '',
        apiBaseUrl: '',
        apiClientId: '',
        apiClientSecret: '',
        adminUsername: '',
        adminPasswd: '',
        require2fa: true,
        logo: null,
      },
      showClientSecret: false,
      showPassword: false,
    };
  },
  methods: {
    async fetchBankDetails() {
    try {
      this.startLoading();
      const response = await FinanceService.getBanksById(this.bankId);
      this.bank = response.data;
    } catch (error) {
      console.error('Failed to load bank details:', error);
      this.$notify({ title: this.$t('finance.banks.failedToLoadBankDetails'), type: 'error' });
    } finally {
      this.stopLoading();
    }
    },
    back() {
      this.$router.go(-1);
    },
    edit() {
      this.$router.push({
        name: 'createBank',
        params: {
          id: this.bankId,
          isEditMode: true,
        },
      });
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text);
      // Optional: Show a snackbar or toast
    },
  },
  mixins: [LoadingMixin],
};

</script>

<style scoped>
</style>